/**
 * Manages the compatible spare parts modal
 */
import {
  createResultsTemplate,
  resultsLoadingTemplate,
  createOptions,
  createDetectedCard,
} from './template';
import { Select } from '../select';
import { NumberInput } from '../number-input';
import { loader } from '../../Foundation';
import { CategorySelection } from './category-selection';

const MODAL_ATTRIBUTE = 'data-compatible-products-modal';

// The accessories endpoint is paged; the modal has no paging UI, so pull the max in one call.
const PAGE_SIZE = 10;

const pagesDropdownClass = 'product-compatible-modal_pagination__dropdown';
const pagesPreviousButtonClass = 'product-compatible-modal_pagination__previous';
const pagesNextButtonClass = 'product-compatible-modal_pagination__next';

export class CompatibleProductsModal {
  constructor(triggerElement, options = null) {
    this.openBtn = triggerElement;
    if (options == null) {
      options = { contentElement: document.querySelector(`[${MODAL_ATTRIBUTE}="modal"]`) };
    }
    if (!triggerElement || !options || !options.contentElement == null) {
      return;
    }

    this.modal =
      typeof options.contentElement === 'string'
        ? document.querySelector(options.contentElement)
        : options.contentElement;
    if (!this.modal) {
      return;
    }

    this.closeBtn = this.modal.querySelector(`[${MODAL_ATTRIBUTE}="close"]`);
    this.cancelBtn = this.modal.querySelector(`[${MODAL_ATTRIBUTE}="cancel"]`);
    this.categorySelection = new CategorySelection();

    this.pagination = null;
    this.paginationCurrentPage = 1;

    if (!this.closeBtn || !this.cancelBtn) {
      console.warn('CompatibleProductsModal: close or cance controls  not attached');
    }

    this.init();
  }

  init() {
    this.attachEventListeners();
    this.initSearch();
  }

  attachEventListeners() {
    this.openBtn.addEventListener('click', () => this.open());
    this.modal.addEventListener('click', (e) => this.handleBackdropClick(e));
    if (this.closeBtn) {
      this.closeBtn.addEventListener('click', () => this.close());
    }

    if (this.cancelBtn) {
      this.cancelBtn.addEventListener('click', () => this.close());
    }
  }

  /**
   * Wires up the cascading identify dropdowns (Family -> Wearing style -> Model).
   * Each change fetches fresh downstream options and the compatible spare parts,
   * rendering everything client-side without a full page refresh.
   */
  initSearch() {
    this.familySelect = this.modal.querySelector('[data-spareparts="family"]');
    this.wearingStyleSelect = this.modal.querySelector('[data-spareparts="wearingStyle"]');
    this.modelSelect = this.modal.querySelector('[data-spareparts="model"]');
    this.detectedEl = this.modal.querySelector('[data-spareparts="detected"]');
    this.hintEl = this.modal.querySelector('.product-compatible-modal__hint');
    this.resultsEl = this.modal.querySelector('[data-spareparts="results"]');

    if (!this.familySelect || !this.wearingStyleSelect || !this.modelSelect || !this.resultsEl) {
      return;
    }

    this.baseUrl = this.modal.getAttribute('data-spareparts-api') || '/api/products';
    this.pageItemId = this.modal.getAttribute('data-page-item-id') || '';

    // Footer cancel only makes sense once results are on screen.
    this.setCancelVisible(false);

    this.familySelect.addEventListener('change', () => this.onFamilyChange());
    this.wearingStyleSelect.addEventListener('change', () => this.onWearingStyleChange());
    this.modelSelect.addEventListener('change', () => this.onModelChange());

    // Add-to-cart buttons live inside the (re-rendered) results grid, so delegate.
    this.resultsEl.addEventListener('click', (e) => {
      const orderBtn = e.target.closest('[data-spareparts="add"]');
      if (orderBtn) {
        e.preventDefault();
        this.addToCart(orderBtn);
      }
    });

    // Reset button lives inside the (re-rendered) detected card, so delegate.
    if (this.detectedEl) {
      this.detectedEl.addEventListener('click', (e) => {
        if (e.target.closest('[data-spareparts="reset"]')) {
          this.reset();
        }
      });
    }

    // Step 1: load families as soon as the modal is created; style & model stay disabled.
    this.loadFamilies();
  }

  loadFamilies() {
    return this.fetchList('families', 'families').then((options) => {
      this.setOptions(this.familySelect, options, 'Select Family', false);
      this.setOptions(this.wearingStyleSelect, [], 'Select Wearing style', true);
      this.setOptions(this.modelSelect, [], 'Select Model', true);
    });
  }

  /**
   * Resets the whole identify form back to its initial state
   * (only Family enabled, nothing selected, no detected card / results).
   */
  reset() {
    this.clearDetected();
    this.clearResults();
    this.categorySelection.hide();
    this.loadFamilies().then(() => this.updateHint());
  }

  /**
   * Populates a native <select>, then (re)builds the design-system Select widget
   * so the dropdown is actually visible (.form-select hides the native element).
   */
  setOptions(select, options, placeholder, disabled) {
    select.innerHTML = createOptions(options, placeholder);
    select.disabled = !!disabled;
    this.enhanceSelect(select);
  }

  enhanceSelect(select) {
    const wrapper = select.closest('.form-select');
    if (!wrapper) return;
    const existing = wrapper.querySelector('.form-select__el');
    if (existing) existing.remove();
    // eslint-disable-next-line no-new
    new Select(wrapper);
  }

  get selection() {
    return {
      family: this.familySelect.value || '',
      wearingStyle: this.wearingStyleSelect.value || '',
      model: this.modelSelect.value || '',
    };
  }

  buildQuery(params) {
    // Array values become repeated params, e.g. categories=Tubes&categories=Domes.
    const parts = Object.keys(params).reduce((acc, key) => {
      const value = params[key];
      if (Array.isArray(value)) {
        value.filter((v) => v).forEach((v) => acc.push(`${key}=${encodeURIComponent(v)}`));
      } else if (value) {
        acc.push(`${key}=${encodeURIComponent(value)}`);
      }
      return acc;
    }, []);
    return parts.length ? `?${parts.join('&')}` : '';
  }

  /**
   * Fetches a dropdown option list from one of the cascading endpoints
   * (families / wearing-styles / models). Always resolves to an array.
   * @param {string} action Endpoint segment, e.g. 'wearing-styles'.
   * @param {string} key Response property holding the string array, e.g. 'wearingStyles'.
   */
  fetchList(action, key, params = {}) {
    const query = this.buildQuery({ itemId: this.pageItemId, ...params });
    return fetch(`${this.baseUrl}/${action}${query}`, { headers: { Accept: 'application/json' } })
      .then((response) => {
        if (!response.ok) {
          throw new Error(`Request failed: ${response.status}`);
        }
        return response.json();
      })
      .then((data) => this.toOptions(data, key))
      .catch((err) => {
        this.resultsEl.innerHTML = `<p class="product-compatible-modal__error">Error: ${err.message}</p>`;
        return [];
      });
  }

  /**
   * The product API returns each dropdown as a flat string array (e.g. { families: [...] }).
   * Map it to the { Value, Label } shape the option template expects.
   */
  toOptions(data, key) {
    const upper = key.charAt(0).toUpperCase() + key.slice(1);
    const list = (data && (data[key] || data[upper])) || [];
    return list.filter((v) => v != null && v !== '').map((v) => ({ Value: v, Label: v }));
  }

  resetSelect(select, placeholder) {
    this.setOptions(select, [], placeholder, true);
  }

  onFamilyChange() {
    const { family } = this.selection;
    // Reset downstream steps whenever family changes.
    this.resetSelect(this.wearingStyleSelect, 'Select Wearing style');
    this.resetSelect(this.modelSelect, 'Select Model');
    this.categorySelection.hide();
    this.clearDetected();
    this.clearResults();
    this.updateHint();

    if (!family) {
      return;
    }

    // Step 2: fetch wearing styles for the chosen family.
    this.fetchList('wearing-styles', 'wearingStyles', { family }).then((options) => {
      this.setOptions(this.wearingStyleSelect, options, 'Select Wearing style', false);
    });
  }

  onWearingStyleChange() {
    const { family, wearingStyle } = this.selection;
    this.resetSelect(this.modelSelect, 'Select Model');
    this.categorySelection.hide();
    this.clearDetected();
    this.clearResults();
    this.updateHint();

    if (!family || !wearingStyle) {
      return;
    }

    // Step 3: fetch models for the chosen family + wearing style.
    this.fetchList('models', 'models', { family, wearingStyle }).then((options) => {
      this.setOptions(this.modelSelect, options, 'Select Model', false);
    });
  }

  onModelChange() {
    // Final step: identify complete -> show the detected hearing aid + compatible parts.
    this.updateHint();
    if (!this.selection.model) {
      this.categorySelection.hide();
      this.clearDetected();
      this.clearResults();

      return;
    }
    // Resolve the (cheap) model info first so the card shows immediately, then load the
    // spare-part categories for the selected model and hand them to the category picker.
    this.loadHearingAid()
      .then(() => this.loadCategories())
      .then((categories) =>
        this.categorySelection.init(
          this.modal,
          this.search.bind(this),
          categories,
          this.clearResults.bind(this),
          this.reset.bind(this),
        ),
      );
  }

  clearDetected() {
    if (this.detectedEl) {
      this.detectedEl.innerHTML = '';
    }
  }

  /**
   * Shows/hides the footer cancel button, which is only relevant while the
   * compatible spare parts results are on screen.
   */
  setCancelVisible(visible) {
    if (this.cancelBtn) {
      this.cancelBtn.style.display = visible ? '' : 'none';
    }
  }

  clearResults() {
    this.resultsEl.innerHTML = '';
    if (this.pagination) this.pagination.innerHTML = '';

    this.setCancelVisible(false);
  }

  /**
   * The "Didn't find your model?" hint is only relevant while the hearing aid
   * is not fully identified, so hide it once all three values are selected.
   */
  updateHint() {
    if (!this.hintEl) return;
    const { family, wearingStyle, model } = this.selection;
    this.hintEl.style.display = family && wearingStyle && model ? 'none' : '';
  }

  loadHearingAid() {
    if (!this.detectedEl) return Promise.resolve();
    const query = this.buildQuery({ itemId: this.pageItemId, model: this.selection.model });
    // model-info returns 404 when the model is unknown; treat that as "nothing to show".
    return fetch(`${this.baseUrl}/model-info${query}`, { headers: { Accept: 'application/json' } })
      .then((response) => (response.ok ? response.json() : null))
      .then((data) => {
        this.detectedEl.innerHTML = createDetectedCard(data);
      })
      .catch(() => {
        this.detectedEl.innerHTML = '';
      });
  }

  /**
   * Loads the spare-part categories (ProductTypeCodes) available for the selected model.
   * Resolves to a [{ id, displayValue }] list for the category picker.
   */
  loadCategories() {
    const query = this.buildQuery({ itemId: this.pageItemId, model: this.selection.model });
    return fetch(`${this.baseUrl}/spare-parts-categories${query}`, {
      headers: { Accept: 'application/json' },
    })
      .then((response) => (response.ok ? response.json() : null))
      .then((data) => {
        const list = (data && (data.categories || data.Categories)) || [];
        return list.filter((v) => v != null && v !== '').map((v) => ({ id: v, displayValue: v }));
      })
      .catch(() => []);
  }

  search(page) {
    const categories = this.categorySelection.getSelectedCategories();
    const query = this.buildQuery({
      itemId: this.pageItemId,
      model: this.selection.model,
      // The accessories endpoint expects a single comma-separated 'categories' value.
      categories: categories.join(','),
      pageSize: PAGE_SIZE,
      page: page ? page : 1,
    });
    this.resultsEl.innerHTML = resultsLoadingTemplate();
    this.setCancelVisible(false);

    fetch(`${this.baseUrl}/accessories${query}`, { headers: { Accept: 'application/json' } })
      .then((response) => {
        if (!response.ok) {
          throw new Error(`Request failed: ${response.status}`);
        }
        return response.json();
      })
      .then((data) => {
        this.resultsEl.innerHTML = createResultsTemplate(data);
        this.initQuantityControls();
        this.setCancelVisible(!!this.resultsEl.querySelector('.product-grid'));

        if (!page && data.paging && data.paging.totalCount > PAGE_SIZE) {
          this.initPagination(data?.paging);
        }
      })
      .catch((err) => {
        this.resultsEl.innerHTML = `<p class="product-compatible-modal__error">Error: ${err.message}</p>`;
        this.setCancelVisible(false);
      });
  }

  initPagination({ totalPages }) {
    let dropdownOptions = '';
    this.paginationCurrentPage = 1;

    for (var i = 1; i < totalPages + 1; i++) {
      dropdownOptions += `<option value="${i}">${i}</option>`;
    }

    const pagesDropdown = `<select id="product-compatible-modal-pagination" class="${pagesDropdownClass}">
            ${dropdownOptions}
        </select>`;

    const pagesPreviousButton = `<button type="button" class="${pagesPreviousButtonClass}" style="visibility:hidden"><</button>`;
    const pagesNextButton = `<button type="button" class="${pagesNextButtonClass}">></button>`;

    this.pagination = document.querySelector('.product-compatible-modal_pagination');
    this.pagination.innerHTML = `${pagesPreviousButton}${pagesDropdown}${pagesNextButton}`;

    document.querySelector(`.${pagesDropdownClass}`).addEventListener('change', (e) => {
      const value = e.target.value;
      this.paginationCurrentPage = e.target.value;
      this.handlePaginationButtons(totalPages);

      this.search(value);
    });

    document.querySelector(`.${pagesNextButtonClass}`).addEventListener('click', () => {
      this.handleNextPage(totalPages);
    });

    document.querySelector(`.${pagesPreviousButtonClass}`).addEventListener('click', () => {
      this.handlePreviousPage(totalPages);
    });
  }

  handleNextPage(totalPages) {
    if (this.paginationCurrentPage < totalPages) {
      this.paginationCurrentPage++;
      this.search(this.paginationCurrentPage);

      document.querySelector(`.${pagesDropdownClass}`).value = this.paginationCurrentPage;
    }

    this.handlePaginationButtons(totalPages);
  }

  handlePreviousPage(totalPages) {
    if (this.paginationCurrentPage > 1) {
      this.paginationCurrentPage--;
      this.search(this.paginationCurrentPage);

      document.querySelector(`.${pagesDropdownClass}`).value = this.paginationCurrentPage;
    }

    this.handlePaginationButtons(totalPages);
  }

  handlePaginationButtons(totalPages) {
    if (this.paginationCurrentPage >= totalPages) {
      document.querySelector(`.${pagesNextButtonClass}`).style.visibility = 'hidden';
    } else {
      document.querySelector(`.${pagesNextButtonClass}`).style.visibility = 'visible';
    }

    if (this.paginationCurrentPage <= 1) {
      document.querySelector(`.${pagesPreviousButtonClass}`).style.visibility = 'hidden';
    } else {
      document.querySelector(`.${pagesPreviousButtonClass}`).style.visibility = 'visible';
    }
  }

  /**
   * Enhances every rendered result card with the shared quantity control
   * (plus / minus stepper), mirroring the ordering page behaviour.
   */
  initQuantityControls() {
    this.resultsEl.querySelectorAll('.number-input').forEach((el) => {
      if (!el.NumberInput) {
        // eslint-disable-next-line no-new
        new NumberInput(el);
      }
    });
  }

  /**
   * Adds the chosen spare part (item number + quantity) to the cart, reusing the
   * same request shape as the ordering page cart endpoints. The endpoint can be
   * overridden via `data-cart-api` on the modal element.
   */
  addToCart(button) {
    const item = button.closest('.product-item');
    if (!item) {
      return;
    }

    const itemNumber = item.getAttribute('data-item-number') || '';
    const quantityInput = item.querySelector('.number-input__field');
    const quantity = quantityInput ? parseInt(quantityInput.value, 10) || 1 : 1;

    if (!itemNumber) {
      return;
    }

    const api = this.modal.getAttribute('data-cart-api') || '/api/cart/cartline/addnonconfigurable';

    loader.show();
    fetch(api, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ ItemNo: itemNumber, Quantity: quantity }),
    })
      .then((response) => {
        if (!response.ok) {
          throw new Error(`Request failed: ${response.status}`);
        }
        return response.json().catch(() => null);
      })
      .then((data) => {
        this.updateCartCount(data, quantity);
        this.showBasketPopup(item, itemNumber, quantity, data);
        this.resetQuantity(item);
      })
      .catch((err) => {
        // eslint-disable-next-line no-console
        console.error('CompatibleProductsModal: add to cart failed', err);
      })
      .finally(() => {
        loader.hide();
      });
  }

  /**
   * Shows the shared "Added to cart" notification (#cartUpdateBox), reusing the
   * global BasketPopup used by the ordering pages so the behaviour matches.
   */
  showBasketPopup(item, itemNumber, quantity, data) {
    if (typeof window.BasketPopup !== 'function') {
      return;
    }

    const image = item.querySelector('.product-item__image img');
    const imageSrc = image ? image.getAttribute('src') : '';
    const title = item.querySelector('.product-item__headline');
    const name = title ? title.textContent.trim() : itemNumber;

    const cartAnchor = this.getCartLinkBadge(data)?.closest('a');
    const cartUrl = this.getCartUrl(data) || cartAnchor?.getAttribute('href') || '#';

    // Badge is handled in updateCartCount; BasketPopup would append one to every service icon.
    window.BasketPopup(imageSrc, itemNumber, name, quantity, 3000, cartUrl, null);
  }

  /**
   * Resets a result card's quantity back to 1 after it has been added to the cart.
   * Dispatches a `focusout` so the shared NumberInput re-syncs its internal state
   * (value + minus/plus disabled states).
   */
  resetQuantity(item) {
    const quantityInput = item.querySelector('.number-input__field');
    if (!quantityInput) {
      return;
    }
    quantityInput.value = '1';
    quantityInput.dispatchEvent(new Event('focusout'));
  }

  /**
   * Reads the updated cart line count from the add-to-cart response.
   */
  cartItemCount(data) {
    if (!data) return NaN;
    const value = data.ItemCount != null ? data.ItemCount : data.itemCount;
    return parseInt(value, 10);
  }

  /**
   * Refreshes the header basket counter without a full page reload, using the
   * total returned by the cart endpoint.
   */
  updateCartCount(data, quantityAdded = 0) {
    const badge = this.getCartBadge(data);
    if (!badge) {
      return;
    }

    const total = this.cartItemCount(data);
    const current = parseInt(badge.textContent, 10) || 0;

    badge.textContent = Number.isNaN(total) ? current + quantityAdded : total;
    badge.classList.remove('is-hidden');
  }

  /**
   * The header only renders the badge when the cart is not empty, so create it
   * on the fly the first time something is added.
   */
  getCartBadge(data) {
    const cartLink = this.getCartLinkBadge(data);
    if (!cartLink) {
      return null;
    }

    const existing = cartLink.querySelector('.service-menu-new__item-badge');
    if (existing) {
      return existing;
    }

    const badge = document.createElement('span');
    badge.className = 'service-menu-new__item-badge';
    badge.textContent = '0';
    cartLink.appendChild(badge);

    return badge;
  }

  /**
   * Service icons share the same markup, so the cart one is matched by comparing
   * its link with the cart URL returned by the add-to-cart endpoint.
   */
  getCartLinkBadge(data) {
    const cartUrl = this.getCartUrl(data);
    if (!cartUrl) {
      return null;
    }

    const target = this.normalizePath(cartUrl);

    return (
      Array.prototype.find.call(
        document.querySelectorAll('.service-menu-new__link-badge'),
        (el) => {
          const href = el.closest('a')?.getAttribute('href');
          return !!href && this.normalizePath(href) === target;
        },
      ) || null
    );
  }

  getCartUrl(data) {
    return (data && (data.CartUrl || data.cartUrl)) || '';
  }

  normalizePath(url) {
    try {
      return new URL(url, window.location.origin).pathname.replace(/\/+$/, '').toLowerCase();
    } catch (e) {
      return String(url).replace(/\/+$/, '').toLowerCase();
    }
  }

  open() {
    this.modal.style.display = 'flex';
    document.body.classList.add('is-modal-open');
    this.previouslyFocused = document.activeElement;
    this.boundHandleKeydown = this.boundHandleKeydown || ((e) => this.handleKeydown(e));
    this.modal.addEventListener('keydown', this.boundHandleKeydown);
    this.focusFirstElement();
  }

  close() {
    this.modal.style.display = 'none';
    document.body.classList.remove('is-modal-open');
    this.reset();
    if (this.boundHandleKeydown) {
      this.modal.removeEventListener('keydown', this.boundHandleKeydown);
    }
    if (this.previouslyFocused && typeof this.previouslyFocused.focus === 'function') {
      this.previouslyFocused.focus();
    }
  }

  handleBackdropClick(event) {
    if (event.target === this.modal) {
      this.close();
    }
  }

  /**
   * Returns the currently visible, tabbable elements inside the modal,
   * used to keep keyboard focus trapped while the modal is open.
   */
  getFocusableElements() {
    const selector =
      'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';
    return Array.prototype.filter.call(
      this.modal.querySelectorAll(selector),
      (el) => el.offsetWidth > 0 || el.offsetHeight > 0 || el === document.activeElement,
    );
  }

  focusFirstElement() {
    const focusable = this.getFocusableElements();
    (focusable[0] || this.modal).focus();
  }

  handleKeydown(event) {
    if (event.key === 'Escape') {
      this.close();
      return;
    }

    if (event.key !== 'Tab') {
      return;
    }

    const focusable = this.getFocusableElements();
    if (!focusable.length) {
      event.preventDefault();
      return;
    }

    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    const active = document.activeElement;

    if (event.shiftKey) {
      if (active === first || !this.modal.contains(active)) {
        event.preventDefault();
        last.focus();
      }
    } else if (active === last || !this.modal.contains(active)) {
      event.preventDefault();
      first.focus();
    }
  }
}
